import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { CircularProgress, IconButton,List,ListItem,Stack, SxProps, Tab, Tabs, TabsOwnProps, Tooltip, Typography, useTheme } from "@mui/material";
import { useKeycloak } from "@react-keycloak/web";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { selectAtom } from "jotai/utils";
import { FC, useState } from "react";
import { AI_GENERATION_PROGRESS, INTERVIEW_NAVIGATION_TABS } from "../../../constants";
import { baseAtom, setSelectedArtifactType } from "../../../stores/jotai/base";
import { discoverySessionAtom } from "../../../stores/jotai/discovery-session";
import { useExportInterviewForTeam } from "../../../tanstackQuery/queries/interviews/useExportInterview";
import { INTERVIEW_LIST_TAB_DIMENSIONS } from "../utils/constants";
import { exportInterviewAtom, setExportInterviewProgress } from '../../../stores/jotai/generation-progress/export/exportInterviewProcess';
import { renderErrorToast } from '../../../../lib/ErrorToastMessage';
import MaterialModal from '../../app/material-modal';
import IwButton from '../../app/IwButton';
interface IInterviewListTabsProps {
    onChange: TabsOwnProps["onChange"];
    value: string;
    sx?: SxProps;
    hideExportButton?:boolean;
    draftInterviewLength?:number;
    publishedInterviewLength?:number;
}
const TEXT_STYLE: SxProps = {
  fontSize: "15px",
  fontFamily: "Inter, sans-serif",
  fontWeight: 500,
  color: "rgba(0, 0, 0, 0.8)",
};
const ExportInterviewModal = ({
  showExportInterviewModal,
  setShowExportInterviewModal,
  isEnableTeamInterviewDownload,
  isTeamInterviewDownloadLoading,
  handleTeamExportInterviews,
}) => {
  const handleExportInterviewModalClose = ()=>{
    setShowExportInterviewModal(false)
  }
  return (
    <MaterialModal
      title="Export All Interviews"
      open={showExportInterviewModal}
      onClose={handleExportInterviewModalClose}
      handleDialogClose={handleExportInterviewModalClose}
      styleOverrides={{
        container: {
          "& .MuiDialog-paper": {
            width: "100%",
            minWidth: "300px",
          },
        },
      }}
    >
      <Stack>
        <Typography sx={{ ...TEXT_STYLE }}>
          You're about to download all interview data, including:
        </Typography>
        <List sx={{ listStyleType: "disc", ml: 4 }}>
          <ListItem sx={{ display: "list-item", p: 0.4 }}>
            Interview details{" "}
          </ListItem>
          <ListItem sx={{ display: "list-item", p: 0.4 }}>Notes </ListItem>
          <ListItem sx={{ display: "list-item", p: 0.4 }}>
            Basic transcriptions{" "}
          </ListItem>
          <ListItem sx={{ display: "list-item", p: 0.4 }}>Insights </ListItem>
        </List>
        <Typography sx={{ ...TEXT_STYLE }}>
          Would you like to proceed?
        </Typography>
        <Stack
          direction="row"
          sx={{
            alignItems: "center",
            justifyContent: "flex-end",
            paddingTop: "8px",
          }}
          spacing={1}
        >
          <IwButton
            variant="outlined"
            onClick={handleExportInterviewModalClose}
          >
            Cancel
          </IwButton>
          <IwButton
            variant="contained"
            disabled={
              isEnableTeamInterviewDownload || isTeamInterviewDownloadLoading
            }
            onClick={handleTeamExportInterviews}
          >
            Download
          </IwButton>
        </Stack>
      </Stack>
    </MaterialModal>
  );
};
const teamAtom =  selectAtom(baseAtom, (base) => base.team)
const InterviewListTabs: FC<IInterviewListTabsProps> = (props) => {
    const { value, onChange, sx, hideExportButton,draftInterviewLength,publishedInterviewLength } = props;
    const setBase = useSetAtom(baseAtom);
    const [team] = useAtom(teamAtom);
    const [discoverySession, setDiscoverySession] = useAtom(discoverySessionAtom);
    const [isEnableTeamInterviewDownload,setIsEnableTeamInterviewDownload]= useState(false)
    const [showExportInterviewModal, setShowExportInterviewModal] = useState(false);
    const { keycloak } = useKeycloak();
    const theme = useTheme();
    const setExportInterviewState=useSetAtom(exportInterviewAtom);
    const exportInterviewState=useAtomValue(exportInterviewAtom);

    const {
      data,
      isLoading: isTeamInterviewDownloadLoading,
      refetch,
      isSuccess,
    } = useExportInterviewForTeam(
      { accessToken: keycloak?.token },
      { groupID: team.groupID, sessionID: discoverySession?.sessionID },
      { enabled: isEnableTeamInterviewDownload }
    );
    const changeArtifactTabOnClick = () => {
        setSelectedArtifactType(setBase, "notes");
    }
    const handleTeamExportInterviews = async () => {
        if (isTeamInterviewDownloadLoading) { // Prevent duplicate or overlapping downloads
            return;
        }
        setIsEnableTeamInterviewDownload(true);      
        try {
            const { data: TeamInterviewDownloadData,isSuccess,isError} = await refetch();
            if (isSuccess) {
                 if (TeamInterviewDownloadData?.generationStarted) {
                  setExportInterviewProgress(
                     setExportInterviewState,
                     team.groupID,
                     AI_GENERATION_PROGRESS.IN_PROGRESS
                   );
                 } else if (!TeamInterviewDownloadData?.generationStarted) {
                  setExportInterviewProgress(
                    setExportInterviewState,
                     team.groupID,
                      AI_GENERATION_PROGRESS.NOT_STARTED
                   );
                 }
            } else {
                renderErrorToast("Export failed: try again later")
            }
        } catch (error) {
            renderErrorToast("Export failed: try again later");
        } finally {
            setIsEnableTeamInterviewDownload(false);
            setShowExportInterviewModal(false)
        }
    };

    return (
      <Stack direction="row" justifyContent="space-between">
        <Tabs
          value={value}
          onChange={onChange}
          className="InterviewListTab"
          sx={{
            minHeight: `${INTERVIEW_LIST_TAB_DIMENSIONS.MIN_HEIGHT}px`,
            height: `${INTERVIEW_LIST_TAB_DIMENSIONS.HEIGHT}px`,
            ".MuiTab-root": {
              textTransform: "capitalize",
              padding: "8px 12px",
              height: `${INTERVIEW_LIST_TAB_DIMENSIONS.HEIGHT}px`,
              minHeight: `${INTERVIEW_LIST_TAB_DIMENSIONS.MIN_HEIGHT}px`,
              fontSize: "1rem",
              "&:hover": {
                color: "#1976D2", 
                borderBottom: "2px solid #1976D2", 
              },
            },
            ".Mui-selected": {
              color: "#1976D2 !important",
              "&:hover":{
                fontWeight: "700 !important",
              }
            },
            ...sx,
          }}
        >
          <Tab
            value={INTERVIEW_NAVIGATION_TABS.PUBLISHED}
            label={`${INTERVIEW_NAVIGATION_TABS.PUBLISHED} (${publishedInterviewLength || 0})`}
            onClick={changeArtifactTabOnClick}
            disabled={publishedInterviewLength === 0}
          />
          <Tab
            value={INTERVIEW_NAVIGATION_TABS.DRAFT}
            label={`${INTERVIEW_NAVIGATION_TABS.DRAFT} (${draftInterviewLength || 0})`}
            onClick={changeArtifactTabOnClick}
            disabled={draftInterviewLength === 0}
          />
        </Tabs>
      <ExportInterviewModal
       showExportInterviewModal = {showExportInterviewModal}
       isEnableTeamInterviewDownload = {isEnableTeamInterviewDownload}
       isTeamInterviewDownloadLoading = {isTeamInterviewDownloadLoading}
       handleTeamExportInterviews = {handleTeamExportInterviews}
       setShowExportInterviewModal={setShowExportInterviewModal}
      />
      </Stack>
    );
};

InterviewListTabs.defaultProps = {
    sx: {},
    hideExportButton: false,
};

export default InterviewListTabs;
